/**
 * TaskCommentReplySection - Seção expansível de comentários/respostas em andamentos
 * Espelha ActivityCommentSection para o módulo de tarefas
 * Reutiliza ActivityCommentInput (já é genérico)
 * Suporta anexos de arquivos nas respostas
 * 
 * @version 1.1.0
 * RELEASE: 2026-04-07 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-11 v1.1.0 - Suporte a anexos em respostas
 * RELEASE: 2026-04-11 v1.2.0 - Preview interno para todos os tipos de anexo (não apenas imagens)
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MessageCircle, ChevronDown, ChevronUp, Trash2, Pencil, CheckCircle2, X, Check, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ActivityCommentInput } from '@/components/crm/activities/ActivityCommentInput';
import { UserMentionPopover } from '@/components/crm/activities/UserMentionPopover';
import { AttachmentThumbnail } from '@/components/shared/AttachmentThumbnail';
import { AttachmentUploadArea } from '@/components/shared/AttachmentUploadArea';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
// Resolve o tipo de mídia do preview interno (imagem/vídeo/documento)
import { resolvePreviewMediaType } from '@/lib/attachment-preview';
import { DownloadAllZipButton } from '@/hooks/useDownloadAllAsZip';
import { useTaskCommentReplies, formatMentionsForDisplay } from '@/hooks/useTaskCommentReplies';
import { validateFile, uploadTaskAttachmentDirect } from '@/hooks/useTaskAttachments';
import { stripMentionIds, extractMentionMap } from '@/hooks/useActivityComments';
import { useTenantMembers } from '@/hooks/useActivityComments';
import { useAuth } from '@/hooks/useAuth';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { getMimeMediaType } from '@/lib/file-utils';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { useToast } from '@/hooks/use-toast';

/** Props do componente */
interface TaskCommentReplySectionProps {
  /** ID do andamento (task_comment) */
  taskCommentId: string;
  /** ID da tarefa (para upload de anexos) */
  taskId: string;
  /** ID do tenant (para upload de anexos) */
  tenantId: string;
  /** Contagem estimada de respostas (antes de carregar) */
  repliesCount?: number;
  /** Se true, a seção inicia expandida por padrão */
  defaultExpanded?: boolean;
  /** Se true, oculta input de resposta e botões de editar/excluir (tarefa fechada) */
  disabled?: boolean;
}

/**
 * Seção de respostas/comentários de um andamento de tarefa
 */
export function TaskCommentReplySection({
  taskCommentId,
  taskId,
  tenantId,
  disabled = false,
  repliesCount = 0,
  defaultExpanded = false,
}: TaskCommentReplySectionProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  // Mapa nome→uuid das menções do conteúdo original (para reconstrução ao salvar)
  const [editMentionMap, setEditMentionMap] = useState<Record<string, string>>({});
  // Estados de edição de anexos de reply
  const [editRemovedAttachmentIds, setEditRemovedAttachmentIds] = useState<string[]>([]);
  const [editStagedFiles, setEditStagedFiles] = useState<File[]>([]);
  const [isUploadingEditFiles, setIsUploadingEditFiles] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { user } = useAuth();
  const { isAdmin } = usePermissionsContext();

  // Estados para @menções no modo edição
  const [editShowMentionPopover, setEditShowMentionPopover] = useState(false);
  const [editMentionSearch, setEditMentionSearch] = useState('');
  const [editCursorPosition, setEditCursorPosition] = useState(0);
  const [editMentionedUsers, setEditMentionedUsers] = useState<Array<{ id: string; full_name: string; avatar_url: string | null }>>([]);

  // Refs espelho para evitar stale closure nos handlers de menção
  const editingContentRef = useRef('');
  const editCursorPositionRef = useRef(0);
  const editMentionedUsersRef = useRef<Array<{ id: string; full_name: string; avatar_url: string | null }>>([]);

  // Membros do tenant para o popover de menções
  const { data: tenantMembers = [] } = useTenantMembers();

  // Hook de respostas (lazy load quando abrir) - agora com taskId para uploads
  const {
    replies,
    isLoading,
    addReply,
    editReply,
    deleteReply,
    markMentionAsRead,
  } = useTaskCommentReplies(isOpen ? taskCommentId : null, taskId);

  const { toast } = useToast();

  /** Handler para enviar resposta com arquivos opcionais */
  const handleSubmitReply = async (content: string, mentionedUserIds: string[], files?: File[]) => {
    await addReply.mutateAsync({ content, mentionedUserIds, files });
  };

  /** Seleciona arquivos para edição de reply */
  const handleEditFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    for (const file of files) {
      const validation = validateFile(file);
      if (!validation.valid) {
        toast({ title: 'Arquivo inválido', description: validation.error, variant: 'destructive' });
      } else {
        validFiles.push(file);
      }
    }
    setEditStagedFiles(prev => [...prev, ...validFiles]);
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  }, [toast]);

  /** Detecta digitação de "@" no textarea de edição para abrir popover de menções */
  const handleEditInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart || 0;
    setEditingContent(value);
    setEditCursorPosition(cursor);
    // Atualiza refs sincronamente para evitar stale closure
    editingContentRef.current = value;
    editCursorPositionRef.current = cursor;

    const textBeforeCursor = value.substring(0, cursor);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      const isTypingNewMention = !textAfterAt.includes(' ');
      // Usa ref para evitar stale closure
      const isExistingMention = editMentionedUsersRef.current.some(u =>
        textAfterAt.startsWith(u.full_name)
      );

      if (isTypingNewMention && !isExistingMention) {
        setEditMentionSearch(textAfterAt);
        setEditShowMentionPopover(true);
        return;
      }
    }

    setEditShowMentionPopover(false);
    setEditMentionSearch('');
  }, []);

  /** Seleciona usuário da menção no modo edição — usa refs para valores sempre frescos */
  const handleEditSelectMention = useCallback((user: { id: string; full_name: string; avatar_url: string | null }) => {
    const currentContent = editingContentRef.current;
    const currentCursor = editCursorPositionRef.current;
    const textBeforeCursor = currentContent.substring(0, currentCursor);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textBeforeAt = currentContent.substring(0, lastAtIndex);
      const textAfterCursor = currentContent.substring(currentCursor);

      const mentionText = `@${user.full_name} `;
      const newContent = textBeforeAt + mentionText + textAfterCursor;

      // Atualiza state E ref sincronamente
      setEditingContent(newContent);
      editingContentRef.current = newContent;
      setEditMentionedUsers(prev => {
        const updated = [...prev.filter(u => u.id !== user.id), user];
        editMentionedUsersRef.current = updated;
        return updated;
      });
      // Atualiza o mapa nome→uuid para reconversão ao salvar
      setEditMentionMap(prev => ({ ...prev, [user.full_name]: user.id }));

      const newCursorPos = lastAtIndex + mentionText.length;
      editCursorPositionRef.current = newCursorPos;
      setTimeout(() => {
        if (editTextareaRef.current) {
          editTextareaRef.current.focus();
          editTextareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }

    setEditShowMentionPopover(false);
    setEditMentionSearch('');
  }, []);

  /**
   * Verifica se o usuário atual foi mencionado e a menção está não lida
   */
  const getUserUnreadMention = (reply: typeof replies[number]) => {
    return reply.mentions?.find(
      m => m.mentioned_user_id === user?.id && !m.read_at
    );
  };

  /** Coleta todos os anexos (imagens, documentos, etc.) para lightbox */
  const getAllMediaItems = (): MediaItem[] => {
    const items: MediaItem[] = [];
    replies.forEach(reply => {
      (reply.attachments || []).forEach(att => {
        items.push({
          url: att.public_url,
          type: resolvePreviewMediaType(att.file_type, (att as any).mime_type) ?? 'document',
          title: att.file_name,
        });
      });
    });
    return items;
  };

  /** Abre lightbox para qualquer tipo de anexo */
  const openLightbox = (url: string) => {
    const items = getAllMediaItems();
    const index = items.findIndex(i => i.url === url);
    if (index >= 0) {
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  // Contagem real (se já carregou) ou estimada
  const displayCount = isOpen ? replies.length : repliesCount;

  return (
    <TooltipProvider>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {/* Header colapsável */}
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "w-full justify-start gap-2 h-7 px-2 text-xs font-normal",
              "text-muted-foreground hover:text-foreground hover:bg-muted/50",
              displayCount > 0 && "text-primary"
            )}
          >
            <MessageCircle className="h-3.5 w-3.5" />
            <span>
              {displayCount > 0
                ? `${displayCount} comentário${displayCount !== 1 ? 's' : ''}`
                : 'Comentar'
              }
            </span>
            {isOpen ? (
              <ChevronUp className="h-3 w-3 ml-auto" />
            ) : (
              <ChevronDown className="h-3 w-3 ml-auto" />
            )}
          </Button>
        </CollapsibleTrigger>

        {/* Conteúdo expansível */}
        <CollapsibleContent className="mt-2 space-y-3">
          {/* Loading */}
          {isLoading && (
            <div className="space-y-2 pl-2">
              {[1, 2].map(i => (
                <div key={i} className="flex gap-2">
                  <Skeleton className="h-6 w-6 rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Lista de respostas */}
          {!isLoading && replies.length > 0 && (
            <div className="space-y-2 pl-2 border-l-2 border-muted ml-2">
              {replies.map((reply) => {
                const unreadMention = getUserUnreadMention(reply);

                return (
                  <div
                    key={reply.id}
                    className={cn(
                      "group flex gap-2 pl-2 py-1 rounded-r-md hover:bg-muted/30 transition-colors",
                      unreadMention && "bg-amber-50 dark:bg-amber-950/20 border-l-2 border-amber-500"
                    )}
                  >
                    {/* Avatar do autor */}
                    <SmartAvatar
                      src={reply.author?.avatar_url}
                      name={reply.author?.full_name}
                      size="xs"
                      className="flex-shrink-0 mt-0.5"
                    />

                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0">
                      {/* Header: Nome + Hora */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-foreground">
                          {reply.author?.full_name || 'Usuário'}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {format(new Date(reply.created_at), "dd/MM HH:mm", { locale: ptBR })}
                          {/* Indicador "(editado)" */}
                          {new Date(reply.updated_at).getTime() > new Date(reply.created_at).getTime() + 1000 && (
                            <span className="ml-1 italic">(editado)</span>
                          )}
                        </span>
                      </div>

                      {/* Texto com menções formatadas OU modo edição */}
                      {editingReplyId === reply.id ? (
                        <div className="mt-1 space-y-1.5">
                          <Textarea
                            ref={editTextareaRef}
                            value={editingContent}
                            onChange={handleEditInputChange}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') setEditShowMentionPopover(false);
                            }}
                            className="min-h-[50px] text-xs"
                            autoFocus
                          />

                          {/* Popover de @menções na edição */}
                          <UserMentionPopover
                            isOpen={editShowMentionPopover}
                            onClose={() => setEditShowMentionPopover(false)}
                            onSelect={handleEditSelectMention}
                            users={tenantMembers}
                            searchTerm={editMentionSearch}
                            anchorRef={editTextareaRef}
                          />

                          {/* Anexos existentes (editáveis — com botão remover) */}
                          {(() => {
                            const existingAtts = (reply.attachments || [])
                              .filter(att => !editRemovedAttachmentIds.includes(att.id));
                            return existingAtts.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {existingAtts.map(att => (
                                  <AttachmentThumbnail
                                    key={att.id}
                                    attachment={att}
                                    readOnly={false}
                                    className="w-12 h-12"
                                    onDelete={() => setEditRemovedAttachmentIds(prev => [...prev, att.id])}
                                  />
                                ))}
                              </div>
                            ) : null;
                          })()}

                          {/* Novos arquivos staged na edição */}
                          {editStagedFiles.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {editStagedFiles.map((file, idx) => (
                                <div key={`edit-staged-${idx}`} className="relative group">
                                  <div className="w-12 h-12 rounded-lg border bg-muted/30 flex items-center justify-center overflow-hidden">
                                    {file.type.startsWith('image/') ? (
                                      <img
                                        src={URL.createObjectURL(file)}
                                        alt={file.name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <span className="text-[8px] text-muted-foreground text-center px-0.5 truncate">
                                        {file.name.split('.').pop()?.toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setEditStagedFiles(prev => prev.filter((_, i) => i !== idx))}
                                    className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-white flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="h-2.5 w-2.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Botão de adicionar anexo na edição */}
                          <button
                            type="button"
                            onClick={() => editFileInputRef.current?.click()}
                            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Upload className="h-3 w-3" />
                            Anexar arquivo
                          </button>
                          <input
                            ref={editFileInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={handleEditFileSelect}
                          />

                          <div className="flex items-center gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-[10px] px-2"
                              onClick={() => {
                                setEditingReplyId(null);
                                setEditingContent('');
                                setEditMentionMap({});
                                setEditRemovedAttachmentIds([]);
                                setEditStagedFiles([]);
                                setEditMentionedUsers([]);
                                setEditShowMentionPopover(false);
                                setEditMentionSearch('');
                                // Limpa refs espelho
                                editingContentRef.current = '';
                                editCursorPositionRef.current = 0;
                                editMentionedUsersRef.current = [];
                              }}
                              disabled={editReply.isPending || isUploadingEditFiles}
                            >
                              <X className="h-3 w-3 mr-0.5" />
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              className="h-6 text-[10px] px-2"
                              onClick={async () => {
                                if (!editingContent.trim()) return;
                                // Reconstrói menções @Nome → @[Nome](uuid) antes de salvar
                                let contentToSave = editingContent.trim();
                                for (const [name, uuid] of Object.entries(editMentionMap)) {
                                  contentToSave = contentToSave.replace(`@${name}`, `@[${name}](${uuid})`);
                                }
                                await editReply.mutateAsync({ replyId: reply.id, content: contentToSave });

                                // Deletar anexos removidos (storage + registro)
                                for (const attachmentId of editRemovedAttachmentIds) {
                                  try {
                                    const { data: att } = await supabase
                                      .from('task_attachments')
                                      .select('storage_path')
                                      .eq('id', attachmentId)
                                      .single();
                                    if (att?.storage_path) {
                                      await supabase.storage.from('tarefas').remove([att.storage_path]);
                                    }
                                    await supabase.from('task_attachments').delete().eq('id', attachmentId);
                                  } catch (err: any) {
                                    logger.error('Erro ao remover anexo de reply na edição:', err.message);
                                  }
                                }

                                // Upload de novos arquivos staged
                                if (editStagedFiles.length > 0) {
                                  setIsUploadingEditFiles(true);
                                  for (const file of editStagedFiles) {
                                    try {
                                      await uploadTaskAttachmentDirect(taskId, tenantId, file, undefined, reply.id);
                                    } catch (err: any) {
                                      logger.error('Erro ao enviar novo anexo de reply na edição:', err.message);
                                    }
                                  }
                                  setIsUploadingEditFiles(false);
                                }

                                // Invalidação final — garante que replies reflitam texto + anexos atualizados
                                queryClient.invalidateQueries({ queryKey: ['task-comment-replies', taskCommentId] });
                                // Mantém aba "Anexos" agregada em sincronia após upload/remoção via resposta
                                queryClient.invalidateQueries({ queryKey: ['task-activity-attachments', taskId] });

                                setEditingReplyId(null);
                                setEditingContent('');
                                setEditMentionMap({});
                                setEditRemovedAttachmentIds([]);
                                setEditStagedFiles([]);
                                setEditMentionedUsers([]);
                                setEditShowMentionPopover(false);
                                setEditMentionSearch('');
                                // Limpa refs espelho
                                editingContentRef.current = '';
                                editCursorPositionRef.current = 0;
                                editMentionedUsersRef.current = [];
                              }}
                              disabled={editReply.isPending || isUploadingEditFiles || !editingContent.trim()}
                            >
                              {(editReply.isPending || isUploadingEditFiles) ? (
                                <Loader2 className="h-3 w-3 animate-spin mr-0.5" />
                              ) : (
                                <Check className="h-3 w-3 mr-0.5" />
                              )}
                              Salvar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p
                          className="text-xs text-foreground mt-0.5 break-words"
                          dangerouslySetInnerHTML={{
                            __html: formatMentionsForDisplay(reply.content)
                          }}
                        />
                      )}

                      {/* Anexos da resposta (modo visualização — somente quando NÃO está editando) */}
                      {editingReplyId !== reply.id && reply.attachments && reply.attachments.length > 0 && (
                        <div className="mt-1.5">
                          {/* Botão para baixar todos os anexos da resposta como ZIP */}
                          <div className="flex justify-end mb-1">
                            <DownloadAllZipButton
                              attachments={reply.attachments}
                              zipFileName={`anexos_resposta_${reply.id.substring(0, 8)}.zip`}
                            />
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {reply.attachments.map(att => (
                              <AttachmentThumbnail
                                key={att.id}
                                attachment={att}
                                readOnly
                                className="w-12 h-12"
                                onClick={() => openLightbox(att.public_url)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ações */}
                    <div className="flex items-center gap-1">
                      {/* Botão de marcar menção como lida */}
                      {unreadMention && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 text-amber-600 hover:text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                              onClick={() => markMentionAsRead.mutate(unreadMention.id)}
                              disabled={markMentionAsRead.isPending}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            Marcar como lida
                          </TooltipContent>
                        </Tooltip>
                      )}

                      {/* Botão editar (autor ou admin, tarefa aberta) */}
                      {!disabled && (reply.author_id === user?.id || isAdmin) && editingReplyId !== reply.id && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                              onClick={() => {
                                setEditingReplyId(reply.id);
                                const strippedContent = stripMentionIds(reply.content);
                                setEditingContent(strippedContent);
                                editingContentRef.current = strippedContent;
                                editCursorPositionRef.current = strippedContent.length;
                                const mentionMap = extractMentionMap(reply.content);
                                setEditMentionMap(mentionMap);
                                // Popula editMentionedUsers com menções existentes + sincroniza ref
                                const existingMentionUsers = Object.entries(mentionMap).map(([name, id]) => ({
                                  id,
                                  full_name: name,
                                  avatar_url: null,
                                }));
                                setEditMentionedUsers(existingMentionUsers);
                                editMentionedUsersRef.current = existingMentionUsers;
                                setEditRemovedAttachmentIds([]);
                                setEditStagedFiles([]);
                                setEditShowMentionPopover(false);
                                setEditMentionSearch('');
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            Editar
                          </TooltipContent>
                        </Tooltip>
                      )}

                      {/* Ação de deletar (autor ou admin, tarefa aberta) */}
                      {!disabled && (reply.author_id === user?.id || isAdmin) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                              onClick={() => deleteReply.mutate(reply.id)}
                              disabled={deleteReply.isPending}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            Excluir comentário
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Input para nova resposta - com suporte a anexos (oculto em tarefas fechadas) */}
          {!disabled && (
            <div className="pl-2">
              <ActivityCommentInput
                onSubmit={handleSubmitReply}
                isSubmitting={addReply.isPending}
                placeholder="Adicione um comentário... Use @ para mencionar"
                allowAttachments
              />
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Lightbox para visualização de imagens */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        mediaItems={getAllMediaItems()}
        initialIndex={lightboxIndex}
      />
    </TooltipProvider>
  );
}
